<script setup lang="ts">
import type { IUser } from '~/components/entity/user/types'
import {
  LucideGlobe,
  LucideMail,
  LucidePhone,
  LucideSmartphone,
  LucideUser
} from 'lucide-vue-next'
import type { IResponseData } from '~/types'

const props = withDefaults(
  defineProps<{
    authorId?: string
    author?: IUser
    enableLink?: boolean
  }>(),
  {
    authorId: '',
    author: () => ({}) as IUser,
    enableLink: false
  }
)

const { data, status } = props.author?.id
  ? { data: ref(null), status: ref('success') }
  : await useApi<IResponseData<IUser[]>>(
      () => useUrlWithParams('/users', { 'filter[id]': props.authorId }),
      {
        key: `author-block-${props.authorId}`,
        server: false
      }
    )

const info = computed((): IUser | null => {
  if (props.author?.id) {
    return props.author
  }
  if (data.value?.data?.length) {
    return data.value.data[0]
  }
  return null
})
</script>

<template>
  <SidebarBlock v-if="info" variant="white">
    <div class="flex items-start gap-3">
      <NuxtLinkLocale v-if="enableLink && info.url" :to="info.url">
        <NuxtImg
          v-if="info.avatar"
          :src="info.avatar"
          :alt="info.fullname"
          class="relative z-10 h-14 w-14 min-w-14 rounded-lg transition-transform duration-300 hover:scale-[3] hover:rounded-[4px]"
        />
        <div
          v-else
          class="flex h-14 w-14 min-w-14 items-center justify-center rounded-lg bg-gray-100"
        >
          <LucideUser class="h-7 w-7" />
        </div>
      </NuxtLinkLocale>
      <figure v-else>
        <NuxtImg
          v-if="info.avatar"
          :src="info.avatar"
          :alt="info.fullname"
          class="relative z-10 h-14 w-14 min-w-14 rounded-lg transition-transform duration-300 hover:scale-[3] hover:rounded-[4px]"
        />
        <div
          v-else
          class="flex h-14 w-14 min-w-14 items-center justify-center rounded-lg bg-gray-100"
        >
          <LucideUser class="h-7 w-7" />
        </div>
      </figure>
      <div>
        <Heading v-if="enableLink && info.url" tag="h3" class="font-serif !text-base !leading-4"
          ><NuxtLinkLocale :to="info.url" class="hover:underline">{{
            info.fullname
          }}</NuxtLinkLocale></Heading
        >
        <Heading v-else tag="h3" class="font-serif !text-base !leading-4">{{
          info.fullname
        }}</Heading>
        <p v-if="info.description" class="mt-1 text-sm leading-4 text-gray-700">
          {{ info.description }}
        </p>
        <p v-if="info.news_count" class="mt-1 text-xs leading-4 text-gray-600">
          {{ $t('common.publications_count') }} {{ info.news_count }}
        </p>
      </div>
    </div>

    <div v-if="info.contacts?.length" class="mt-4 grid grid-cols-1 space-y-4">
      <div class="flex flex-col space-y-3 rounded-lg">
        <template v-for="(contact, index) in info.contacts" :key="index">
          <template v-if="'mobile_phone' in contact || 'office_phone' in contact">
            <a
              :href="`tel:${contact.mobile_phone || contact.office_phone}`"
              class="flex items-center gap-2 hover:text-blue-600"
            >
              <div class="flex size-9 items-center justify-center rounded-lg bg-gray-100">
                <LucideSmartphone v-if="contact.mobile_phone" class="h-5 w-5" />
                <LucidePhone v-else class="h-5 w-5" />
              </div>
              <span class="w-full truncate text-sm font-semibold leading-3">{{
                contact.mobile_phone || contact.office_phone
              }}</span>
            </a>
          </template>

          <template v-if="'email' in contact">
            <a
              :href="`mailto:${contact.email}`"
              class="flex items-center gap-2 hover:text-blue-600"
            >
              <div class="flex size-9 items-center justify-center rounded-lg bg-gray-100">
                <LucideMail class="h-5 w-5" />
              </div>
              <span class="w-full truncate text-sm font-semibold leading-3">{{ contact.email }}</span>
            </a>
          </template>

          <template v-if="'website_url' in contact">
            <a
              :href="
                contact.website_url?.startsWith('http')
                  ? contact.website_url
                  : `https://${contact.website_url}`
              "
              class="flex items-center gap-2 hover:text-blue-600"
              target="_blank"
              rel="noopener noreferrer"
            >
              <div class="flex size-9 items-center justify-center rounded-lg bg-gray-100">
                <LucideGlobe class="h-5 w-5" />
              </div>
              <span class="w-full truncate text-sm font-semibold leading-3">{{ contact.website_url }}</span>
            </a>
          </template>

          <template v-if="'telegram' in contact">
            <a
              :href="`https://t.me/${contact.telegram}`"
              class="flex items-center gap-2 hover:text-blue-600"
              target="_blank"
            >
              <i class="flex size-9 min-w-9 items-center justify-center rounded-lg bg-gray-100 [&>svg]:size-5"><UiIcon name="telegram" /></i>
              <span class="w-full truncate text-sm font-semibold leading-3">{{ contact.telegram }}</span>
            </a>
          </template>

          <template v-if="'whatsapp' in contact">
            <a
              :href="`https://wa.me/${contact.whatsapp}`"
              class="flex items-center gap-2 hover:text-green-500"
              target="_blank"
            >
              <i class="flex size-9 min-w-9 items-center justify-center rounded-lg bg-gray-100 [&>svg]:size-5"><UiIcon name="whatsapp" /></i>
              <span class="w-full truncate text-sm font-semibold leading-3">{{ contact.whatsapp }}</span>
            </a>
          </template>
        </template>
      </div>
    </div>
  </SidebarBlock>

  <SidebarBlock v-else-if="status !== 'success'" variant="white">
    <div class="animate-pulse space-y-5">
      <div class="flex items-center gap-3">
        <div class="h-16 w-16 rounded-lg bg-gray-200" />
        <div class="flex-1 space-y-2">
          <div class="h-3 w-2/3 rounded bg-gray-200" />
          <div class="h-2 w-1/3 rounded bg-gray-200" />
        </div>
      </div>

      <div class="mt-4 space-y-3">
        <div class="flex items-center gap-2">
          <div class="size-9 rounded-lg bg-gray-200" />
          <div class="h-3 w-1/2 rounded bg-gray-200" />
        </div>
        <div class="flex items-center gap-2">
          <div class="size-9 rounded-lg bg-gray-200" />
          <div class="h-3 w-2/3 rounded bg-gray-200" />
        </div>
        <div class="flex items-center gap-2">
          <div class="size-9 rounded-lg bg-gray-200" />
          <div class="h-3 w-1/3 rounded bg-gray-200" />
        </div>
      </div>
    </div>
  </SidebarBlock>
</template>
